import React, { useState, useEffect } from 'react';
import {
  Menu,
  MenuItem,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { useNavigate, useLocation } from 'react-router-dom';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import SecurityIcon from '@material-ui/icons/Security';
import { useSplitTreatments } from '@splitsoftware/splitio-react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuPaper: {
      marginTop: theme.spacing(1),
      zIndex: 99999,
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 200,
      padding: theme.spacing(1.5, 2),
    },
    icon: {
      marginRight: theme.spacing(1),
    },
  })
);

export const LeaderboardTabWithDropdown = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const spanRef = React.useRef<HTMLSpanElement>(null);
  const splitTreatments = useSplitTreatments({
    names: ['devx-ui-feature-enable'],
  });

  const showCybersecurityLeaderboard = React.useMemo(() => {
    const config =
      splitTreatments?.treatments?.['devx-ui-feature-enable']?.config;

    if (!config) {
      return true;
    }

    try {
      const parsedConfig = JSON.parse(config);
      const flagValue = parsedConfig?.cybersecurityLeaderboardEnabled;

      if (typeof flagValue === 'boolean') {
        return flagValue;
      }

      if (typeof flagValue === 'string') {
        return flagValue.toLowerCase() === 'true';
      }
    } catch (e) {
      return true;
    }

    return true;
  }, [splitTreatments?.treatments]);

  useEffect(() => {
    if (location.pathname === '/leaderboard') {
      navigate('/discover', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    // Find the parent tab button and prevent its navigation behavior only
    if (spanRef.current) {
      const tabButton = spanRef.current.closest('button');
      const tabAnchor = spanRef.current.closest('a');
      
      const handleTabClick = (e: Event) => {
        e.preventDefault();
        // Don't stopPropagation so our React handler can still fire
      };
      
      if (tabButton) {
        tabButton.addEventListener('click', handleTabClick, false);
      }
      if (tabAnchor) {
        tabAnchor.addEventListener('click', handleTabClick, false);
      }
      
      return () => {
        if (tabButton) {
          tabButton.removeEventListener('click', handleTabClick, false);
        }
        if (tabAnchor) {
          tabAnchor.removeEventListener('click', handleTabClick, false);
        }
      };
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Opening dropdown, current anchorEl:', anchorEl);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDevXLeaderboard = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigate('/leaderboard/devx');
    handleClose();
  };

  const handleCybersecurityLeaderboard = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigate('/leaderboard/cybersecurity');
    handleClose();
  };

  return (
    <>
      <span 
        ref={spanRef}
        onClick={handleClick}
        style={{ 
          cursor: 'pointer',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        LEADERBOARD
      </span>
      <Menu
        id="leaderboard-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        classes={{
          paper: classes.menuPaper,
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleDevXLeaderboard} className={classes.menuItem}>
          <EmojiEventsIcon className={classes.icon} />
          DevX Leaderboard
        </MenuItem>
        {showCybersecurityLeaderboard && (
          <MenuItem
            onClick={handleCybersecurityLeaderboard}
            className={classes.menuItem}
          >
            <SecurityIcon className={classes.icon} />
            Cybersecurity Application Security Leaderboard
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
